import { PlanningFilters } from '@/services/PlanningService';
import {
  PageHeaderFilterControl,
  PageHeaderFilterOption,
} from '@/types/model/pageHeaderBar';

export type PlanningLineItemFilterOption = 'active' | 'inactive';
export type PlanningInspectionPlanFilterOption = 'active' | 'inactive' | 'locked' | 'unlocked';

const LINE_ITEM_FILTER_OPTIONS: PageHeaderFilterOption<PlanningLineItemFilterOption>[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const INSPECTION_PLAN_FILTER_OPTIONS: PageHeaderFilterOption<PlanningInspectionPlanFilterOption>[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Locked', value: 'locked' },
  { label: 'Unlocked', value: 'unlocked' },
];

const truncate = (value: string, maxLength: number = 20): string => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, Math.max(0, maxLength - 3))}...`;
};

const selectedToText = (values: string[]): string => {
  if (values.length === 0) {
    return 'None';
  }

  return truncate(values.join(', '));
};

export const buildPlanningFilterSummary = (filters: PlanningFilters): string => {
  if (filters.showAll) {
    return 'All';
  }

  const lineItemSelections = toLineItemFilterSelection(filters);
  const inspectionPlanSelections = toInspectionPlanFilterSelection(filters);

  return `Line Items: ${selectedToText(lineItemSelections)} | Inspection Plans: ${selectedToText(inspectionPlanSelections)}`;
};

const toLineItemFilterSelection = (filters: PlanningFilters): PlanningLineItemFilterOption[] => {
  return [
    filters.showActiveLineItems ? 'active' : null,
    filters.showInactiveLineItems ? 'inactive' : null,
  ].filter((value): value is PlanningLineItemFilterOption => value !== null);
};

const toInspectionPlanFilterSelection = (filters: PlanningFilters): PlanningInspectionPlanFilterOption[] => {
  return [
    filters.showActiveInspectionPlans ? 'active' : null,
    filters.showInactiveInspectionPlans ? 'inactive' : null,
    filters.showLockedInspectionPlans ? 'locked' : null,
    filters.showUnlockedInspectionPlans ? 'unlocked' : null,
  ].filter((value): value is PlanningInspectionPlanFilterOption => value !== null);
};

const getShowAllState = (nextFilters: PlanningFilters): boolean => {
  return (
    nextFilters.showActiveLineItems
    && nextFilters.showInactiveLineItems
    && nextFilters.showActiveInspectionPlans
    && nextFilters.showInactiveInspectionPlans
    && nextFilters.showLockedInspectionPlans
    && nextFilters.showUnlockedInspectionPlans
  );
};

const getSelectValues = <TValue extends string>(
  controls: PageHeaderFilterControl[],
  controlId: string
): TValue[] => {
  const control = controls.find((candidate) => candidate.id === controlId);
  if (!control || control.type !== 'select') {
    return [];
  }

  const value = control.value;
  return (Array.isArray(value) ? value : [value]) as TValue[];
};

const getToggleValue = (controls: PageHeaderFilterControl[], controlId: string): boolean | null => {
  const control = controls.find((candidate) => candidate.id === controlId);
  if (!control || control.type !== 'toggle') {
    return null;
  }

  return control.checked;
};

export const buildPlanningFilterControls = (filters: PlanningFilters): PageHeaderFilterControl[] => {
  const selectedLineItemFilters = toLineItemFilterSelection(filters);
  const selectedInspectionPlanFilters = toInspectionPlanFilterSelection(filters);

  return [
    {
      type: 'toggle',
      id: 'planning-show-all-filter',
      label: 'Show All',
      checked: filters.showAll,
    },
    {
      type: 'select',
      id: 'planning-line-items-filter',
      label: 'Line Items',
      value: selectedLineItemFilters,
      options: LINE_ITEM_FILTER_OPTIONS,
      multiple: true,
      disabled: false,
      disabledWhen: {
        controlId: 'planning-show-all-filter',
        checked: true,
      },
      selectedText: selectedToText(selectedLineItemFilters),
    },
    {
      type: 'select',
      id: 'planning-inspection-plans-filter',
      label: 'Inspection Plans',
      value: selectedInspectionPlanFilters,
      options: INSPECTION_PLAN_FILTER_OPTIONS,
      multiple: true,
      disabled: false,
      disabledWhen: {
        controlId: 'planning-show-all-filter',
        checked: true,
      },
      selectedText: selectedToText(selectedInspectionPlanFilters),
    },
  ];
};

export const mapPlanningControlsToFilters = (
  controls: PageHeaderFilterControl[],
  currentFilters: PlanningFilters
): PlanningFilters => {
  const showAll = getToggleValue(controls, 'planning-show-all-filter') ?? currentFilters.showAll;

  if (showAll) {
    return {
      ...currentFilters,
      showAll: true,
      showActiveLineItems: true,
      showInactiveLineItems: true,
      showActiveInspectionPlans: true,
      showInactiveInspectionPlans: true,
      showLockedInspectionPlans: true,
      showUnlockedInspectionPlans: true,
    };
  }

  const lineItemSelections = getSelectValues<PlanningLineItemFilterOption>(controls, 'planning-line-items-filter');
  const inspectionPlanSelections = getSelectValues<PlanningInspectionPlanFilterOption>(controls, 'planning-inspection-plans-filter');

  const nextFilters: PlanningFilters = {
    ...currentFilters,
    showAll: false,
    showActiveLineItems: lineItemSelections.includes('active'),
    showInactiveLineItems: lineItemSelections.includes('inactive'),
    showActiveInspectionPlans: inspectionPlanSelections.includes('active'),
    showInactiveInspectionPlans: inspectionPlanSelections.includes('inactive'),
    showLockedInspectionPlans: inspectionPlanSelections.includes('locked'),
    showUnlockedInspectionPlans: inspectionPlanSelections.includes('unlocked'),
  };

  return {
    ...nextFilters,
    showAll: getShowAllState(nextFilters),
  };
};

